/**
 * EditProfileBasicInfoSection
 *
 * The "Basic info" card of `EditUserProfilePage` — name, username (with its
 * availability affordances) and bio. Port of Flutter's `basicInfoSection()`
 * (`lib/presentation/screens/user/edit_user_profile_screen.dart:472-476` renders
 * it second in the scroll body, under the profile image).
 *
 * Extracted verbatim from the page in Task 11 (the page crossed the 300-line
 * ceiling once the profile-image section landed). Presentational and stateless:
 * every value and mutator is owned by `useEditUserProfile`.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { InputText } from '@/presentation/components/common/InputText';
import { InputLengthFormatters } from '@/presentation/constants/formatters/inputLengthFormatters';
import { useEditProfileSectionStyles } from '../hooks/useEditProfileSectionStyles';

export interface EditProfileBasicInfoSectionProps {
  name: string;
  username: string;
  bio: string;
  isGeneratingUsername: boolean;
  usernameExists: boolean;
  onNameChange: (value: string) => void;
  onUsernameChange: (value: string) => void;
  onBioChange: (value: string) => void;
  onNameBlur: () => void;
  onUsernameBlur: () => void;
}

export const EditProfileBasicInfoSection: React.FC<EditProfileBasicInfoSectionProps> = ({
  name,
  username,
  bio,
  isGeneratingUsername,
  usernameExists,
  onNameChange,
  onUsernameChange,
  onBioChange,
  onNameBlur,
  onUsernameBlur,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const { labelStyle, sectionHeaderStyle, cardStyle, rowStyle, separatorStyle } =
    useEditProfileSectionStyles();

  return (
    <div style={{ marginBottom: responsive.spacing.lg }}>
      <p style={sectionHeaderStyle}>{l10n.editUserProfile.basicInfoSection}</p>
      <div style={cardStyle}>
        {/* Name field */}
        <div style={rowStyle}>
          <span style={labelStyle}>{l10n.editUserProfile.nameLabel}</span>
          <InputText
            qa={{ id: 'edit-profile-name' }}
            multiline={false}
            value={name}
            onChange={onNameChange}
            maxLength={InputLengthFormatters.nameMaxLength}
            onBlur={onNameBlur}
            placeholder={l10n.editUserProfile.namePlaceholder}
            backgroundColor={theme.transparent}
            textColor={theme.text.primary}
            fontSize={responsive.font.base}
            style={{ flex: 1, minWidth: 0 }}
          />
        </div>

        <div style={separatorStyle} />

        {/* Username field */}
        <div style={rowStyle}>
          <span style={labelStyle}>{l10n.editUserProfile.usernameLabel}</span>
          <InputText
            qa={{ id: 'edit-profile-username' }}
            multiline={false}
            value={username}
            onChange={(v) => onUsernameChange(v.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
            maxLength={InputLengthFormatters.usernameMaxLength}
            onBlur={onUsernameBlur}
            placeholder={l10n.editUserProfile.usernamePlaceholder}
            backgroundColor={theme.transparent}
            textColor={theme.text.primary}
            fontSize={responsive.font.base}
            style={{ flex: 1, minWidth: 0 }}
          />
          {isGeneratingUsername && (
            <ExpauseLoadingIndicator size={responsive.icon.small} />
          )}
          {!isGeneratingUsername && username.trim().length > 0 && (
            <AssetIcon
              path={`/assets/images/${usernameExists ? 'error' : 'check'}.png`}
              color={usernameExists ? theme.activeRed : theme.exclusive}
              width={responsive.icon.small}
              height={responsive.icon.small}
              style={{ flexShrink: 0 }}
            />
          )}
        </div>

        {usernameExists && username.trim().length > 0 && (
          <p style={{
            color: theme.accent,
            fontSize: responsive.font.xs,
            paddingLeft: responsive.spacing.base,
            paddingRight: responsive.spacing.base,
            paddingBottom: responsive.spacing.sm,
            margin: 0,
          }}>
            {l10n.editUserProfile.usernameNotAvailable}
          </p>
        )}

        <div style={separatorStyle} />

        {/* Bio field */}
        <div style={{ ...rowStyle, alignItems: 'flex-start' }}>
          <span
            style={{
              ...labelStyle,
              paddingTop: responsive.spacing.xxs
            }}>
            {l10n.editUserProfile.bioLabel}
          </span>
          <InputText
            qa={{ id: 'edit-profile-bio' }}
            value={bio}
            onChange={onBioChange}
            maxLength={InputLengthFormatters.userBioMaxLength}
            rows={3}
            placeholder={l10n.editUserProfile.bioPlaceholder}
            backgroundColor={theme.transparent}
            height={responsive.form.textareaHeight}
            style={{ flex: 1, minWidth: 0 }}
          />
        </div>
      </div>

      <p style={{
        color: theme.text.secondary,
        fontSize: responsive.font.xs,
        marginTop: responsive.spacing.xs,
        paddingLeft: responsive.spacing.xs
      }}>
        {l10n.editUserProfile.usernameHint}
      </p>
    </div>
  );
};
